import { Component, inject, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { LoginSelector, SettingsService, UserService, ThemeService } from 'army-builder-shared';
import { ViewWillEnter } from '@ionic/angular';

@Component({
    standalone: false,
    selector: 'fww-home',
    template: `
        <ng-container
            *ngLet="{
                loginState: login$ | async,
                loginChecked: loginChecked$ | async
            } as props"
        >
            <ion-toolbar>
                <ion-buttons slot="end">
                    <ion-menu-button *ngIf="props.loginState.user"></ion-menu-button>
                </ion-buttons>
            </ion-toolbar>

            <div class="homeContainer">
                <div class="logoWrap">
                    <div class="logo"></div>
                </div>

                <div class="menu">
                    <div class="tile welcomeTile" *ngIf="props.loginState.user && props.loginChecked">
                        <p class="welcomeMsg">
                            {{ 'GLOBAL.LOGIN.LOGGED_IN_MESSAGE' | translate: props.loginState.user }}
                        </p>
                    </div>

                    <abs-billing-issue-warning subscriptionUrl="/subscription/subscribe"></abs-billing-issue-warning>

                    <div class="gameModeGrid" *ngIf="props.loginState.user">
                        <div class="tile tile--interactive gameModeCard ffCard" [routerLink]="['/ff']">
                            <div class="cardContent">
                                <div class="cardTop">
                                    <h3 class="tile-title gameModeTitle">{{ 'GLOBAL.MENU_ITEMS.FF' | translate }}</h3>
                                </div>
                            </div>
                            <img
                                class="cardMiniature cardMiniature--right"
                                src="/assets/images/home-king-of-the-park.webp"
                                alt="King of the Park"
                            />
                        </div>

                        <div class="tile tile--interactive gameModeCard fwwCard" [routerLink]="['/fww']">
                            <img
                                class="cardMiniature cardMiniature--left"
                                src="/assets/images/home-deathclaw-matriarch.webp"
                                alt="Deathclaw Matriarch"
                            />
                            <div class="cardContent">
                                <div class="cardTop">
                                    <h3 class="tile-title gameModeTitle">{{ 'GLOBAL.MENU_ITEMS.FWW' | translate }}</h3>
                                </div>
                            </div>
                        </div>

                        <div class="tile tile--interactive gameModeCard fww2eCard" [routerLink]="['/fww2e']" *featureFlag="'fww2e'">
                            <div class="cardContent">
                                <div class="cardTop">
                                    <h3 class="tile-title gameModeTitle">{{ 'GLOBAL.MENU_ITEMS.FWW2E' | translate }}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <abs-login [showLogout]="!themeService.isDesktop()"></abs-login>

                <!-- Tertiary content links: anchor to the bottom of the page -->
                <footer class="contentLinks">
                    <a [routerLink]="['/pages/patch-notes']">{{ 'GLOBAL.MENU_ITEMS.PATCH_NOTES' | translate }}</a>
                    <a [routerLink]="['/pages/known-issues']">{{ 'GLOBAL.MENU_ITEMS.KNOWN_ISSUES' | translate }}</a>
                    <a [routerLink]="['/pages/about']">{{ 'GLOBAL.MENU_ITEMS.ABOUT' | translate }}</a>
                </footer>
            </div>
        </ng-container>
    `,
    styles: [
        `
            :host {
                background: #1a1a1a;
                display: flex;
                flex-direction: column;
                overflow: auto;
                contain: unset; /* Fixes an issue with the background image not covering the full viewport height at certain screen sizes. */
            }

            :host::before {
                content: '';
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: url(/assets/images/home-bg.png) center center/cover no-repeat;
                opacity: 0.3;
                z-index: 0;
            }

            .homeContainer {
                position: relative;
                z-index: 1;
                display: flex;
                flex-direction: column;
                max-width: 1400px;
                margin: 0 auto;
                width: 100%;
                padding: 0 20px;
                flex: 1 1 auto;
                justify-content: space-evenly;
            }

            .logoWrap {
                flex: 0 0 auto;
                padding: 0 0 24px;
            }

            .logo {
                background: url(/assets/images/logo.webp) center no-repeat;
                background-size: contain;
                height: 150px;
                width: 100%;
                max-width: 600px;
                margin: 0 auto;
            }

            ion-toolbar {
                --ion-toolbar-background: transparent;
                --background: transparent;
                background: transparent;
                position: relative;
                z-index: 2;
            }

            ion-menu-button {
                color: #fff;
            }

            .menu {
                flex: 0 0 auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 10px;
                width: 100%;
            }

            .welcomeTile {
                width: 100%;
                max-width: 800px;
                background: rgba(255, 255, 255, 0.92);
                border-color: rgba(255, 255, 255, 0.4);
                margin-bottom: 40px;
            }

            .welcomeMsg {
                margin: 0;
                font-size: 0.85em;
                color: #333;
                text-align: center;
            }

            .gameModeGrid {
                display: grid;
                grid-template-columns: 1fr;
                align-items: stretch;
                gap: 10px;
                width: 100%;
                max-width: 800px;
                /* top padding lets images bleed upward without hitting the welcome tile */
                padding-top: 44px;
                margin-top: -44px;
            }

            .gameModeCard {
                position: relative;
                overflow: visible; /* allow the miniature to break out */
                display: flex;
                align-items: stretch;
                align-self: stretch;
                height: 100%;
                min-height: 86px;
                padding: 0;
                /* override the default tile white surface */
                background: rgba(20, 20, 20, 0.85);
                border-color: rgba(255, 255, 255, 0.1);
                backdrop-filter: blur(6px);
            }

            .cardContent {
                flex: 1;
                padding: 14px 14px 14px 14px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: 6px;
                /* ensure text isn't hidden under the overflowing image */
                position: relative;
                z-index: 1;
            }

            /* leave room for each image on its respective side.
               Images are 400px wide; at 130px tall:
                 KOTP (400×438):       130 × (400/438) ≈ 119px wide
                 Matriarch (400×460):  130 × (400/460) ≈ 113px wide
               Use 120px padding to keep text clear with a small buffer. */
            .ffCard .cardContent {
                padding-right: 120px;
            }
            .fwwCard .cardContent {
                padding-left: 120px;
            }

            .cardMiniature {
                position: absolute;
                bottom: -6px;
                height: 130px;
                width: auto;
                pointer-events: none;
                -webkit-user-drag: none;
                user-select: none;
                z-index: 2;
            }

            .cardMiniature--right {
                right: -4px;
            }
            .cardMiniature--left {
                left: -4px;
            }

            .cardTop {
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                gap: 8px;
                margin-bottom: 4px;
            }

            .gameModeTitle {
                font-family: 'BarlowCondensed', sans-serif;
                font-size: 1.5em;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.06em;
                color: #fff;
            }

            .cardArrow {
                font-size: 1.2em;
                color: rgba(255, 255, 255, 0.35);
                flex-shrink: 0;
                margin-top: 3px;
                transition: color 0.15s ease;
            }

            .gameModeCard:hover {
                background: rgba(30, 30, 30, 0.95);
            }

            .gameModeCard:hover .cardArrow {
                color: rgba(255, 255, 255, 0.8);
            }

            .contentLinks {
                display: flex;
                justify-content: center;
                gap: 4px 0;
                padding: 20px 0 8px;
                width: 100%;
                max-width: 600px;
                margin: 0 auto;
                white-space: nowrap;
            }

            .contentLinks a {
                color: rgba(255, 255, 255, 0.55);
                text-decoration: none;
                font-size: 0.7em;
                text-transform: uppercase;
                letter-spacing: 0.1em;
                padding: 2px 10px;
                transition: color 0.15s ease;
                flex: 0 1 auto;
            }

            .contentLinks a:hover {
                color: rgba(255, 255, 255, 0.85);
            }

            .contentLinks a + a {
                border-left: 1px solid rgba(255, 255, 255, 0.15);
            }

            abs-login {
                width: 100%;
                margin: 0 auto;
            }

            @media screen and (min-width: 501px) {
                .welcomeTile {
                    width: 460px;
                }

                abs-login {
                    width: 460px;
                }

                .gameModeGrid {
                    width: 460px;
                }
            }

            @media screen and (min-width: 992px) {
                .homeContainer {
                    padding: 0 40px;
                }

                .menu {
                    padding: 40px 0 60px;
                }

                :host::before {
                    opacity: 0.2;
                }

                ion-toolbar {
                    display: none;
                }

                .logo {
                    height: 200px;
                }

                .welcomeTile {
                    max-width: 1000px;
                }

                /* Side-by-side cards at desktop */
                .gameModeGrid {
                    grid-template-columns: 1fr 1fr;
                    max-width: 1000px;
                    /* increase bleed budget to match taller images */
                    padding-top: 60px;
                    margin-top: -60px;
                    width: auto;
                }

                .gameModeCard {
                    min-height: 140px;
                }

                /* Images are 400px wide; at 180px tall:
                   KOTP (400×438):      180 × (400/438) ≈ 164px wide
                   Matriarch (400×460): 180 × (400/460) ≈ 157px wide
                   Use 170px padding for comfortable clearance. */
                .cardMiniature {
                    height: 180px;
                }

                .ffCard .cardContent {
                    padding-right: 170px;
                }

                .fwwCard .cardContent {
                    padding-left: 170px;
                }

                .gameModeTitle {
                    font-size: 2em;
                }
            }
        `
    ]
})
export class HomePage implements OnInit, ViewWillEnter {
    login$ = this.store.pipe(select(LoginSelector));
    loginChecked$ = this.userService.loginChecked$;
    settingsService = inject(SettingsService);
    constructor(
        public userService: UserService,
        private store: Store,
        public themeService: ThemeService
    ) {}

    ngOnInit() {
        this.ionViewWillEnter();
    }

    ionViewWillEnter() {
        // abs-page-layout normally updates the theme from route data, but HomePage
        // doesn't use it — so we explicitly reset every time this page becomes active
        // to prevent the ff/fww class lingering on the router outlet after navigating back.
        this.themeService.selectTheme('');
    }

    logout() {
        this.userService.logout();
    }
}
